import type {
  RichTextEditorProps,
  ActionButtonProps,
} from 'src/types/sense-ai-panel';
import * as React from 'react';
import {Map} from 'immutable';

import {
  Editor,
  EditorState,
  convertFromRaw,
  convertToRaw,
  RichUtils,
  getDefaultKeyBinding,
} from 'draft-js';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {FormLabelSmall} from '@spaced-out/ui-design-system/lib/components/Text';
import {colorBackgroundPrimary} from '@spaced-out/ui-design-system/lib/styles/variables/_color';

import css from './rich-text-editor.css';


type ButtonData = {
  ariaLabel: string;
  iconRightName: string;
  onClick: () => void;
  tooltipContent: string;
};

const ActionButton = ({
  tooltipContent,
  onClick,
  ...props
}: ActionButtonProps) => (
  <Tooltip
    body={<span>{tooltipContent}</span>}
    placement="top"
    delayMotionDuration="fast"
  >
    <Button
      {...props}
      onClick={(event) => {
        event.preventDefault();
        onClick();
      }}
      size="small"
      type="ghost"
    />
  </Tooltip>
);

const RichTextEditor = ({
  value,
  height = '178px',
  bgColor = colorBackgroundPrimary,
  borderRadius = '8px',
  placeholder,
  onChange,
  disabled = false,
  label,
  // @ts-expect-error - TS2322 - Type '() => void' is not assignable to type '() => Promise<undefined> | undefined'.
  handleRewrite = () => {},
  required = false,
  showRewriteButton = false,
  readOnly = false,
  format = 'richText',
}: RichTextEditorProps) => {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const editorRef = React.useRef();

  const [editorState, setEditorState] = React.useState(
    EditorState.createEmpty(),
  );
  const [textAlign, setTextAlign] = React.useState<'left' | 'center' | 'right'>(
    'left',
  );

  const handleDraftChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    const contentState = newEditorState.getCurrentContent();
    const contentStateJson = JSON.stringify(convertToRaw(contentState));
    const contentToPass =
      format === 'richText'
        ? contentStateJson
        : JSON.parse(contentStateJson)
            // @ts-expect-error - TS7006 - Parameter 'block' implicitly has an 'any' type.
            .blocks.map((block) => block.text)
            .join('\n');
    onChange?.(contentToPass);
  };

  const handleAlign = (alignment: 'left' | 'center' | 'right') => {
    setTextAlign(alignment);
  };

  const handleStyle = (style: string) => {
    const newEditorState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(newEditorState);
  };

  const handleHeader = (headerType: 'header-one' | 'header-two') => {
    const newEditorState = RichUtils.toggleBlockType(editorState, headerType);
    setEditorState(newEditorState);
  };

  const handleList = (
    listType: 'unordered-list-item' | 'ordered-list-item',
  ) => {
    const newEditorState = RichUtils.toggleBlockType(editorState, listType);
    setEditorState(newEditorState);
  };

  const createButtonData = (
    label: string,
    icon: string,
    handler: () => void,
    content: string,
  ): ButtonData => ({
    ariaLabel: `${label} Button`,
    iconRightName: icon,
    onClick: handler,
    tooltipContent: content,
  });

  const actionButtonsData: Array<ButtonData> = [
    createButtonData('Bold', 'bold', () => handleStyle('BOLD'), 'Bold'),
    createButtonData('Italic', 'italic', () => handleStyle('ITALIC'), 'Italic'),
    createButtonData(
      'Underline',
      'underline',
      () => handleStyle('UNDERLINE'),
      'Underline',
    ),
    createButtonData(
      'Align Left',
      'align-left',
      () => handleAlign('left'),
      'Align Left',
    ),
    createButtonData(
      'Align Center',
      'align-center',
      () => handleAlign('center'),
      'Align Center',
    ),
    createButtonData(
      'Align Right',
      'align-right',
      () => handleAlign('right'),
      'Align Right',
    ),
    createButtonData('H1', 'h1', () => handleHeader('header-one'), 'H1'),
    createButtonData('H2', 'h2', () => handleHeader('header-two'), 'H2'),
    createButtonData(
      'UL',
      'list-ul',
      () => handleList('unordered-list-item'),
      'Unordered List',
    ),
    createButtonData(
      'OL',
      'list-ol',
      () => handleList('ordered-list-item'),
      'Ordered List',
    ),
  ];

  const onFocus = () => {
    const editor = editorRef.current;
    if (editor) {
      // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
      editor.focus();
    }
  };

  React.useEffect(() => {
    if (value) {
      setEditorState(() => {
        try {
          const contentState = convertFromRaw(JSON.parse(value));
          return EditorState.createWithContent(contentState);
        } catch (error: any) {
          // @ts-expect-error - TS2551 - Property 'createWithText' does not exist on type 'typeof EditorState'. Did you mean 'createWithContent'?
          return EditorState.createWithText(value);
        }
      });
    }
  }, []);

  return (
    <>
      <div
        className={css.parentContainer}
        style={{
          '--textArea-height': height,
          '--editor-border-radius': borderRadius,
          '--textArea-bg-color': bgColor,
        }}
        onFocus={onFocus}
      >
        {label && (
          <div className={css.infoContent}>
            <FormLabelSmall color="secondary">{label}</FormLabelSmall>
            &nbsp;
            {required && <FormLabelSmall color="danger">{'*'}</FormLabelSmall>}
          </div>
        )}
        <div className={css.container}>
          <div className={css.draftEditorContainer}>
            {/* @ts-expect-error - TS2786 - 'Editor' cannot be used as a JSX component. */}
            <Editor
              editorState={editorState}
              onChange={handleDraftChange}
              placeholder={placeholder}
              tabIndex={0}
              textAlignment={textAlign}
              readOnly={readOnly}
              // @ts-expect-error - TS2769 - No overload matches this call.
              ref={editorRef}
            />
          </div>
        </div>
        {!readOnly && (
          <div className={css.actions}>
            <div>
              {actionButtonsData.map((buttonData, index) => (
                <ActionButton key={index} {...buttonData} disabled={disabled} />
              ))}
            </div>
            {showRewriteButton && (
              <Button
                type="secondary"
                iconLeftName="refresh"
                onClick={() => handleRewrite()}
              >
                Rewrite
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RichTextEditor;
